/**
 * LPA Activation Code utilities.
 *
 * Per GSMA SGP.22 v2.x the LPA Activation Code format is:
 *
 *   LPA:<scheme_version>$<SMDP+_address>$<Activation_Code>[$<Conf_Required>[$<OID>]]
 *
 * Field values are forbidden from containing the `$` separator. SMDP+ is a
 * DNS-style domain, Activation Code is hex / base32. So a positional split
 * on the first two `$` separators is reliable for spec-compliant strings.
 *
 * This module is the single source of truth for parsing LPA strings into
 * their constituent SM-DP+ + Activation Code parts. ManualCard +
 * ManualCardPlaceholder both call `parseLpa` so they stay consistent.
 *
 * Fail-soft contract: if anything looks off (missing prefix, missing
 * fields, weird characters in the SMDP+ slot, Activation Code too short),
 * `parseLpa` returns `null`. Callers must hide the split-out UI rather
 * than show possibly-wrong values — better to leave the user with the
 * full LPA than feed them a wrong SMDP+ that fails install.
 */

export type ParsedLpa = {
  smdp: string
  activationCode: string
}

/**
 * Activation Code length safety floor. Real values are typically 32 hex
 * chars (128-bit matching id) or longer. Anything under 4 chars is
 * almost certainly not an Activation Code — treat as parse failure.
 */
const MIN_ACTIVATION_CODE_LEN = 4

export function parseLpa(lpa: unknown): ParsedLpa | null {
  if (typeof lpa !== "string") return null
  const trimmed = lpa.trim()
  if (!trimmed) return null

  // Accept `LPA:1$…` and case variants. The regex captures the first two
  // `$`-delimited fields and stops — anything after them (confirmation
  // flag, OID) is irrelevant for the split UI.
  //
  // [^$\s]+ in each capture group means "no `$`, no whitespace" — both
  // are illegal in spec-compliant field values, so refusing them rejects
  // the malformed strings rather than silently mis-splitting them.
  const match = trimmed.match(/^LPA:\d+\$([^$\s]+)\$([^$\s]+)/i)
  if (!match) return null

  const smdp = match[1].trim()
  const activationCode = match[2].trim()

  // SMDP+ should look like a hostname — at minimum it has to contain a
  // dot. Without this an attacker could craft an LPA where SMDP+ is a
  // single token; we'd rather show no split than show a wrong one.
  if (!smdp.includes(".")) return null
  if (activationCode.length < MIN_ACTIVATION_CODE_LEN) return null

  return { smdp, activationCode }
}

/**
 * A non-cryptographic hash used to fingerprint LPA strings in analytics
 * events without exposing the actual code. Just enough entropy to tell
 * "is the same bad LPA failing repeatedly?" apart from "many different
 * bad LPAs failing once each".
 */
export function hashLpaPrefix(lpa: string, prefixLen = 24): string {
  const prefix = lpa.slice(0, prefixLen)
  let h = 5381
  for (let i = 0; i < prefix.length; i += 1) {
    // eslint-disable-next-line no-bitwise
    h = (h * 33) ^ prefix.charCodeAt(i)
  }
  // eslint-disable-next-line no-bitwise
  return (h >>> 0).toString(16)
}
